import { CUSTOM_INSTRUCTION_MAX_LENGTH } from '../constants/instructions';
import type { TInstructionKey } from '../microservice-types/db/instruction';
import { instructionLength } from './instructions';

export interface InstructionEditorState
{
    draft: string;
    selection: TInstructionKey | null;
    dirty: boolean;
    saving: boolean;
    error: 'save' | 'tooLong' | null;
}

/** Orders autosaves independently of either client's Angular version. */
export class InstructionEditor
{
    state: InstructionEditorState = this.emptyState();
    private timer: ReturnType<typeof setTimeout> | undefined;
    private pending: (() => Promise<void>) | null = null;
    private failed: (() => Promise<void>) | null = null;
    private tail: Promise<void> = Promise.resolve();
    private revision = 0;
    private generation = 0;
    private composing = false;

    constructor(private readonly changed: (state: InstructionEditorState) => void) {}

    hydrate(draft: string): void
    {
        if (!this.state.dirty && !this.state.saving && !this.state.error) this.update({ draft, selection: null });
    }

    setComposing(composing: boolean): void
    {
        this.composing = composing;
        if (composing) this.cancelPending();
    }

    edit(draft: string, save: (text: string) => Promise<void>): void
    {
        this.cancelPending();
        const revision = ++this.revision;
        const tooLong = instructionLength(draft) > CUSTOM_INSTRUCTION_MAX_LENGTH;
        this.update({ draft, dirty: true, saving: false, selection: draft.trim() ? 'custom' : 'formal', error: tooLong ? 'tooLong' : null });
        this.failed = null;
        if (tooLong || this.composing) return;
        this.pending = this.job(() => save(draft), revision);
        this.timer = setTimeout(() => this.enqueuePending(), 300);
    }

    select(selection: TInstructionKey, save: () => Promise<void>): void
    {
        this.cancelPending();
        const revision = ++this.revision;
        this.failed = null;
        this.update({ selection, dirty: false, error: null });
        this.pending = this.job(save, revision);
        this.enqueuePending();
    }

    async flush(): Promise<void>
    {
        if (this.composing) throw new Error('Finish composing the instruction before sending');
        this.enqueuePending();
        let waiting: Promise<void>;
        do { waiting = this.tail; await waiting; } while (waiting !== this.tail);
        if (this.state.error) throw new Error(`Instruction ${this.state.error}`);
    }

    async retry(): Promise<void>
    {
        if (this.failed)
        {
            this.pending = this.failed;
            this.failed = null;
            this.update({ error: null });
        }
        await this.flush();
    }

    reset(): void
    {
        this.cancelPending();
        this.generation++;
        this.composing = false;
        this.revision++;
        this.tail = Promise.resolve();
        this.failed = null;
        this.state = this.emptyState();
        this.changed(this.state);
    }

    private job(save: () => Promise<void>, revision: number): () => Promise<void>
    {
        const generation = this.generation;
        const run = async () =>
        {
            if (generation !== this.generation) return;
            if (revision === this.revision) this.update({ saving: true });
            try
            {
                await save();
                if (generation === this.generation && revision === this.revision)
                {
                    this.failed = null;
                    this.update({ dirty: false, error: null });
                }
            }
            catch
            {
                if (generation === this.generation && revision === this.revision)
                {
                    this.failed = run;
                    this.update({ error: 'save' });
                }
            }
            finally
            {
                if (generation === this.generation && revision === this.revision) this.update({ saving: false });
            }
        };
        return run;
    }

    private enqueuePending(): void
    {
        clearTimeout(this.timer);
        this.timer = undefined;
        if (!this.pending) return;
        const pending = this.pending;
        this.pending = null;
        this.update({ saving: true });
        this.tail = this.tail.then(pending);
    }

    private cancelPending(): void
    {
        clearTimeout(this.timer);
        this.timer = undefined;
        this.pending = null;
    }

    private update(patch: Partial<InstructionEditorState>): void
    {
        this.state = { ...this.state, ...patch };
        this.changed(this.state);
    }

    private emptyState(): InstructionEditorState
    {
        return { draft: '', selection: null, dirty: false, saving: false, error: null };
    }
}
